import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { LegacyInvoiceDialog } from "@/components/LegacyInvoiceDialog";
import { ArrowRightLeft, CheckCircle2, ChevronDown, ChevronUp, FileUp, X } from "lucide-react";

const DISMISS_KEY = "migrationCardDismissed";

/** Billing-page card: how many active customers are fully moved off the old system. */
export function MigrationProgressCard({ refreshSignal, onChanged }) {
    const [data, setData] = useState(null);
    const [showMissing, setShowMissing] = useState(false);
    const [uploadFor, setUploadFor] = useState(null);
    const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/billing/migration-progress");
            setData(data);
        } catch { /* card is non-critical */ }
    }, []);
    useEffect(() => { load(); }, [load, refreshSignal]);

    if (!data || !data.total_active) return null;
    const pct = Math.round((data.migrated / data.total_active) * 100);
    const complete = data.migrated === data.total_active;
    if (complete && dismissed) return null;

    return (
        <Card data-testid="migration-progress-card">
            <CardContent className="p-5 space-y-4">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                        {complete ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                            : <ArrowRightLeft className="w-5 h-5 text-primary" />}
                        <div>
                            <div className="font-heading font-semibold" data-testid="migration-title">
                                {complete ? "Migration complete" : "Migration progress"}
                            </div>
                            <div className="text-xs text-muted-foreground" data-testid="migration-summary">
                                <b className="text-foreground">{data.migrated}</b> of {data.total_active} active customers
                                have a legacy invoice and a scheduled next invoice
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="text-2xl font-bold tabular" data-testid="migration-percent">{pct}%</div>
                        {complete && (
                            <Button variant="ghost" size="sm" data-testid="migration-dismiss-btn"
                                    onClick={() => { localStorage.setItem(DISMISS_KEY, "1"); setDismissed(true); }}>
                                <X className="w-4 h-4" />
                            </Button>
                        )}
                    </div>
                </div>
                <Progress value={pct} data-testid="migration-progress-bar" />
                <div className="grid grid-cols-3 gap-3 text-center">
                    <Stat label="Legacy invoice" value={data.with_legacy} testid="migration-with-legacy" />
                    <Stat label="Next invoice scheduled" value={data.with_next_billing} testid="migration-with-next" />
                    <Stat label="Still missing" value={data.missing_count} testid="migration-missing-count"
                          accent={data.missing_count ? "text-amber-600" : ""} />
                </div>
                {!complete && (
                    <Button variant="outline" size="sm" onClick={() => setShowMissing((v) => !v)}
                            data-testid="migration-toggle-missing">
                        {showMissing ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                        {showMissing ? "Hide missing" : `Show missing (${data.missing_count})`}
                    </Button>
                )}
                {showMissing && !complete && (
                    <MissingList items={data.missing} total={data.missing_count} onUpload={setUploadFor} />
                )}
            </CardContent>
            <LegacyInvoiceDialog open={!!uploadFor} onOpenChange={(v) => !v && setUploadFor(null)}
                                 customer={uploadFor} onCreated={() => { load(); onChanged?.(); }} />
        </Card>
    );
}

function MissingList({ items, total, onUpload }) {
    return (
        <div className="border border-border rounded-md divide-y max-h-80 overflow-y-auto" data-testid="migration-missing-list">
            {items.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`migration-missing-${c.id}`}>
                    <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{c.name}</div>
                        <div className="text-[11px] text-muted-foreground">
                            {c.account_number} · {c.has_legacy ? "has legacy invoice" : "no legacy invoice"} ·{" "}
                            {c.has_next ? "next invoice scheduled" : "no next invoice"}
                        </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => onUpload(c)}
                            data-testid={`migration-upload-${c.id}`}>
                        <FileUp className="w-3.5 h-3.5 mr-1" />Upload
                    </Button>
                </div>
            ))}
            {total > items.length && (
                <div className="px-3 py-2 text-xs text-muted-foreground">Showing first {items.length} of {total}</div>
            )}
        </div>
    );
}

function Stat({ label, value, testid, accent = "" }) {
    return (
        <div className="border border-border rounded p-2 bg-secondary/30">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className={`text-xl font-bold tabular ${accent}`} data-testid={testid}>{value}</div>
        </div>
    );
}
